import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import {
  ArrowRight,
  Bot,
  Check,
  CheckCircle2,
  ChevronDown,
  Headphones,
  Megaphone,
  MessageCircleMore,
  Search,
  ShoppingBag,
  ShieldCheck,
  Sparkles,
  Trash2,
  Workflow,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  Attribution,
  CheckoutResponse,
  CommerceCatalog,
  CommerceProduct,
  CommerceQuote,
  PublicOrder,
} from "../../models/commerce.model";
import {
  createCommerceCheckout,
  getCommerceCatalog,
  getCommerceOrder,
  getCommerceQuote,
  trackCommerceEvent,
  verifyCommercePayment,
} from "../../services/commerce.service";
import { trackAnalyticsEvent } from "../../services/firebase.service";
import {
  trackFacebookCustomEvent,
  trackInitiateCheckoutEvent,
  trackPurchaseEvent,
  trackViewContentEvent,
} from "../../utilities/facebook.utility";
import "./ShopKaBotStorefront.css";
import "@fontsource/manrope/400.css";
import "@fontsource/manrope/500.css";
import "@fontsource/manrope/600.css";
import "@fontsource/manrope/700.css";
import "@fontsource/manrope/800.css";

const DURATIONS = [1, 3, 6, 12];
const ACTIVE_ORDER_KEY = "shopkabot_active_order";
const ATTRIBUTION_KEY = "shopkabot_attribution";
const CHECKOUT_SESSION_KEY = "shopkabot_checkout_session";

type BuyEvent =
  | "buy_page_visit"
  | "one_month_trial_cta_clicked"
  | "see_how_it_works_cta_clicked"
  | "order_creation_clicked"
  | "checkout_initiated"
  | "purchase_completed";

const trackBuyEvent = (
  event: BuyEvent,
  params: Record<string, string | number> = {},
) => {
  void trackAnalyticsEvent(event, { page_path: "/buy", ...params });
};

const trackTrialClick = () => trackBuyEvent("one_month_trial_cta_clicked");

const CATEGORIES = [
  { code: "all", name: "All plans" },
  { code: "marketing", name: "Marketing" },
  { code: "chatbot", name: "Chatbot automation" },
  { code: "agentic_ai", name: "Agentic AI" },
];

const money = (paise = 0) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: paise % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(paise / 100);

const monthlyEquivalentMoney = (paise = 0) => money(Math.floor(paise / 100) * 100);

const discountFor = (product: CommerceProduct, price?: CommerceProduct["prices"][number]) => {
  const monthlyPrice = product.prices.find((candidate) => candidate.durationMonths === 1);
  if (!price?.amountPaise || !monthlyPrice?.amountPaise || price.durationMonths === 1) return null;
  const regularTotalPaise = monthlyPrice.amountPaise * price.durationMonths;
  const savingsPaise = regularTotalPaise - price.amountPaise;
  if (savingsPaise <= 0) return null;
  return {
    savingsPaise,
    percentage: Math.round((savingsPaise / regularTotalPaise) * 100),
  };
};

const newId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(20);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
};

const captureAttribution = (): Attribution => {
  const params = new URLSearchParams(window.location.search);
  let stored: Partial<Attribution> = {};
  try {
    stored = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || "{}") || {};
  } catch {
    stored = {};
  }
  const query = (name: string, fallback = "") => params.get(name) || fallback;
  const result: Attribution = {
    anonymousSessionId: stored.anonymousSessionId || newId(),
    utmSource: query("utm_source", stored.utmSource),
    utmMedium: query("utm_medium", stored.utmMedium),
    utmCampaign: query("utm_campaign", stored.utmCampaign),
    utmContent: query("utm_content", stored.utmContent),
    utmTerm: query("utm_term", stored.utmTerm),
    metaCampaignId: query(
      "campaign_id",
      query("meta_campaign_id", stored.metaCampaignId),
    ),
    metaAdSetId: query(
      "adset_id",
      query("meta_adset_id", stored.metaAdSetId),
    ),
    metaAdId: query("ad_id", query("meta_ad_id", stored.metaAdId)),
    fbclid: query("fbclid", stored.fbclid),
    referralSource: stored.referralSource || document.referrer || "direct",
    landingPage: stored.landingPage || window.location.href.slice(0, 500),
  };
  localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(result));
  return result;
};

const apiError = (error: unknown, fallback: string) => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "data" in error.response
  ) {
    const data = error.response.data as { detail?: string };
    if (data.detail) return data.detail;
  }
  return error instanceof Error ? error.message : fallback;
};

const loadRazorpay = () =>
  new Promise<void>((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-shopkabot-razorpay="true"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Secure payment could not load.")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.dataset.shopkabotRazorpay = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Secure payment could not load. Check your connection and try again."));
    document.body.appendChild(script);
  });

function StoreHeader({ count, onCart }: { count: number; onCart: () => void }) {
  return (
    <header className="commerce-header">
      <Link className="commerce-brand" to="/" aria-label="ShopKaBot home">
        <img src="/shopkabot-wordmark.svg" alt="ShopKaBot" width="160" height="40" />
      </Link>
      <nav aria-label="Purchase navigation">
        <a href="#plans">Shop plans</a>
        <a href="#setup">How it works</a>
        <a href="#faq">Buying guide</a>
      </nav>
      <button className="commerce-cart-button" type="button" onClick={onCart} aria-label={`Open cart, ${count} ${count === 1 ? "plan" : "plans"}`}>
        <ShoppingBag size={19} aria-hidden="true" /><span>Cart</span><b>{count}</b>
      </button>
    </header>
  );
}

function ProductArtwork({ code }: { code: string }) {
  const Icon = code === "marketing" ? Megaphone : code === "chatbot" ? Workflow : Bot;
  return (
    <div className={`commerce-product-art commerce-art-${code}`} aria-hidden="true">
      <span className="commerce-art-icon"><Icon size={28} strokeWidth={1.6} /></span>
      <div className="commerce-art-window">
        <span className="commerce-art-window-title"><i /><i /><i /></span>
        {code === "marketing" ? <><div className="commerce-art-message"><Megaphone size={16} /><span>Keep your customers in the loop</span></div><div className="commerce-art-bars"><i /><i /><i /><i /><i /><i /><i /></div><span className="commerce-art-caption">Campaigns that keep you connected</span></>
          : code === "chatbot" ? <><div className="commerce-art-bubble">How can we help today?</div><div className="commerce-art-options"><span>Explore products</span><span>Track an order</span></div><span className="commerce-art-caption"><CheckCircle2 size={14} /> A conversation, taken care of</span></>
            : <><div className="commerce-art-message"><Sparkles size={18} /><span>Your business knowledge.<br />A more capable assistant.</span></div><div className="commerce-art-tools"><span>Knowledge</span><span>Tools</span><span>Actions</span></div><span className="commerce-art-caption">Connected to the way you work</span></>}
      </div>
    </div>
  );
}

function PlanCard({ product, duration, marketingFeatures, inCart, onChoose, onCart }: {
  product: CommerceProduct; duration: number; marketingFeatures: string[];
  inCart: boolean; onChoose: () => void; onCart: () => void;
}) {
  const chosen = product.prices.find((price) => price.durationMonths === duration);
  const fallback = product.prices.find((price) => price.available);
  const shown = chosen?.available ? chosen : fallback;
  const discount = discountFor(product, shown);
  const features = product.code === "chatbot"
    ? ["Everything required from Marketing", ...product.features.filter((feature) => !marketingFeatures.includes(feature))]
    : product.features;
  return (
    <article className={`commerce-plan-card ${product.recommended ? "recommended" : ""}`} id={product.recommended ? "most-popular-plan" : undefined} aria-labelledby={`plan-${product.code}`}>
      <div className="commerce-card-art-wrap">
        <ProductArtwork code={product.code} />
        {product.recommended ? <span className="commerce-popular"><Sparkles size={13} aria-hidden="true" /> Most popular</span> : null}
        {!fallback ? <span className="commerce-availability">Coming soon</span> : null}
      </div>
      <div className="commerce-card-content">
        <p className="commerce-plan-eyebrow">{product.eyebrow}</p>
        <h3 id={`plan-${product.code}`}>{product.name}</h3>
        <p className="commerce-plan-description">{product.code === "agentic_ai" ? "AI-powered conversations and approved actions grounded in your business knowledge." : product.description}</p>
        <div className="commerce-price-block">
          {shown?.amountPaise !== undefined ? <>
            <p className="commerce-plan-price">{money(shown.amountPaise)}<span> / {shown.durationMonths === 1 ? "1-month trial" : `${shown.durationMonths} months`}</span></p>
            <p className="commerce-plan-payable">{monthlyEquivalentMoney(shown.monthlyEquivalentPaise)} / month equivalent, before GST</p>
            <p className="commerce-setup-price">{shown.setupFeePaise ? `${money(shown.setupFeePaise)} setup fee, before GST` : "Setup included"}</p>
            {discount ? <p className="commerce-plan-savings">Save {money(discount.savingsPaise)} ({discount.percentage}%)</p> : null}
          </> : <><p className="commerce-plan-price commerce-custom-price">Pricing coming soon</p><p className="commerce-plan-payable">Online pricing is being finalized.</p></>}
        </div>
        <button className={`commerce-button ${product.recommended || inCart ? "commerce-button-primary" : "commerce-button-outline"}`} type="button" disabled={!fallback} onClick={inCart ? onCart : onChoose} aria-label={fallback ? `${inCart ? "View" : "Add"} ${product.name} ${inCart ? "in" : "to"} cart` : `${product.name} coming soon`}>
          {inCart ? <Check size={18} aria-hidden="true" /> : <ShoppingBag size={18} aria-hidden="true" />}{!fallback ? "Coming soon" : inCart ? "View in cart" : "Add to cart"}
        </button>
        {shown && shown.durationMonths !== duration ? <p className="commerce-card-note">Available as a {shown.durationMonths}-month plan.</p> : null}
        <ul className="commerce-card-features">{features.map((feature) => <li key={feature}><Check size={16} aria-hidden="true" />{feature}</li>)}</ul>
      </div>
    </article>
  );
}

function ShopKaBotStorefront() {
  const navigate = useNavigate();
  const attribution = useMemo(captureAttribution, []);
  const [catalog, setCatalog] = useState<CommerceCatalog | null>(null);
  const [catalogError, setCatalogError] = useState("");
  const [duration, setDuration] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<CommerceProduct | null>(null);
  const [quote, setQuote] = useState<CommerceQuote | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [successOrder, setSuccessOrder] = useState<PublicOrder | null>(null);
  const [checkoutError, setCheckoutError] = useState("");
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [heroPassed, setHeroPassed] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const quoteRequest = useRef(0);
  const browsePlansButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (cartOpen && !selectedProduct) browsePlansButton.current?.focus();
  }, [cartOpen, selectedProduct]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError("");
    try {
      setCatalog(await getCommerceCatalog());
    } catch (error) {
      setCatalogError(apiError(error, "Plans are temporarily unavailable."));
    } finally {
      setCatalogLoading(false);
    }
  }, []);
  const visitTracked = useRef(false);

  useEffect(() => {
    if (!visitTracked.current) {
      visitTracked.current = true;
      trackBuyEvent("buy_page_visit");
    }
    trackCommerceEvent("landing_view", attribution);
    trackViewContentEvent({
      content_name: "ShopKaBot plans",
      content_type: "product_group",
      currency: "INR",
    });
    void loadCatalog();

    let stored: { orderId?: string; statusToken?: string } = {};
    try {
      stored = JSON.parse(localStorage.getItem(ACTIVE_ORDER_KEY) || "{}") || {};
    } catch {
      stored = {};
    }
    if (stored.orderId && stored.statusToken) {
      getCommerceOrder(stored.orderId, stored.statusToken)
        .then((order) => {
          if (order.status === "PAID") setSuccessOrder(order);
        })
        .catch(() => undefined);
    }
  }, [attribution, loadCatalog]);

  useEffect(() => {
    const hero = document.getElementById("commerce-hero");
    if (!hero || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroPassed(!entry.isIntersecting),
      { threshold: 0.08 },
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const plans = document.getElementById("plans");
    if (!plans || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackCommerceEvent("pricing_view", attribution);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(plans);
    return () => observer.disconnect();
  }, [attribution]);

  const priceFor = (product: CommerceProduct, months = duration) =>
    product.prices.find((price) => price.durationMonths === months);
  const visibleProducts = catalog?.products.filter((product) => {
    const text = [product.name, product.description, product.eyebrow, ...product.features].join(" ").toLowerCase();
    return (category === "all" || product.code === category) && text.includes(search.trim().toLowerCase());
  }) || [];
  const marketingFeatures = catalog?.products.find((product) => product.code === "marketing")?.features || [];

  const loadPlanQuote = async (product: CommerceProduct, months: number) => {
    const request = ++quoteRequest.current;
    setQuoteLoading(true);
    setQuoteError("");
    try {
      const nextQuote = await getCommerceQuote(product.code, months);
      if (request !== quoteRequest.current) return;
      setQuote(nextQuote);
      trackCommerceEvent("checkout_started", attribution, { productCode: product.code, durationMonths: months });
    } catch (error) {
      if (request === quoteRequest.current) setQuoteError(apiError(error, "Unable to calculate this order. Please try again."));
    } finally {
      if (request === quoteRequest.current) setQuoteLoading(false);
    }
  };

  const removeFromCart = () => {
    quoteRequest.current += 1;
    setSelectedProduct(null);
    setQuote(null);
    setQuoteLoading(false);
    setQuoteError("");
    setCheckoutError("");
    setCoupon("");
  };

  const chooseProduct = async (product: CommerceProduct) => {
    const selectedPrice = priceFor(product);
    const purchasablePrice = selectedPrice?.available
      ? selectedPrice
      : product.prices.find((price) => price.available);
    if (!purchasablePrice) return;
    const selectedDuration = purchasablePrice.durationMonths;
    setDuration(selectedDuration);
    setSelectedProduct(product);
    setCheckoutError("");
    setCoupon("");
    setQuote(null);
    setCartOpen(true);
    trackCommerceEvent("plan_selected", attribution, {
      productCode: product.code,
      durationMonths: selectedDuration,
    });
    trackBuyEvent("order_creation_clicked", {
      product_code: product.code,
      duration_months: selectedDuration,
    });
    trackInitiateCheckoutEvent({
      value: (purchasablePrice.amountPaise || 0) / 100,
      currency: "INR",
      content_name: product.name,
      content_type: "product",
      duration_months: selectedDuration,
    });
    await loadPlanQuote(product, selectedDuration);
  };

  const applyCoupon = async () => {
    if (!selectedProduct) return;
    setBusy(true);
    setCheckoutError("");
    try {
      setQuote(await getCommerceQuote(selectedProduct.code, duration, coupon.trim()));
    } catch (error) {
      setCheckoutError(apiError(error, "The coupon could not be applied."));
    } finally {
      setBusy(false);
    }
  };

  const handlePayment = async (checkout: CheckoutResponse) => {
    const payment = checkout.order.payment;
    if (!payment) throw new Error("Secure payment is unavailable for this order.");
    await loadRazorpay();
    const instance = new window.Razorpay({
      key: payment.keyId,
      amount: payment.amountPaise,
      currency: payment.currency,
      name: "ShopKaBot",
      description: `${checkout.order.product.name} · ${checkout.order.product.durationMonths}-month plan`,
      order_id: payment.providerOrderId,
      prefill: payment.prefill,
      theme: { color: "#059669" },
      modal: {
        ondismiss: () => {
          setBusy(false);
          setCheckoutError("Payment was not completed. Your order is saved so you can try again.");
        },
      },
      handler: async (result) => {
        try {
          const order = await verifyCommercePayment({
            orderId: checkout.order.orderId,
            statusToken: checkout.statusToken,
            razorpayPaymentId: result.razorpay_payment_id,
            razorpayOrderId: result.razorpay_order_id,
            razorpaySignature: result.razorpay_signature,
          });
          if (order.status === "PAID") {
            setCheckoutOpen(false);
            setSuccessOrder(order);
            sessionStorage.removeItem(CHECKOUT_SESSION_KEY);
            trackCommerceEvent("purchase_completed", attribution, {
              productCode: order.product.code,
              durationMonths: order.product.durationMonths,
            });
            trackBuyEvent("purchase_completed", {
              transaction_id: order.orderId,
              product_code: order.product.code,
              value: Number(order.amounts.totalPaise || 0) / 100,
              currency: "INR",
              duration_months: order.product.durationMonths,
            });
            trackPurchaseEvent({
              value: Number(order.amounts.totalPaise || 0) / 100,
              currency: "INR",
              content_name: order.product.name,
              content_type: "product",
              duration_months: order.product.durationMonths,
            });
          } else {
            setCheckoutError("Payment is processing. Your plan will activate after Razorpay confirms it.");
          }
        } catch (error) {
          setCheckoutError(apiError(error, "Payment verification is still pending."));
          trackCommerceEvent("checkout_error", attribution, {
            productCode: checkout.order.product.code,
            durationMonths: checkout.order.product.durationMonths,
          });
        } finally {
          setBusy(false);
        }
      },
    });
    instance.on("payment.failed", (failure) => {
      setCheckoutError(
        failure.error?.description || "Payment failed. No plan was activated; please try again.",
      );
      setBusy(false);
    });
    instance.open();
  };

  const selectDuration = (months: number) => {
    setDuration(months);
    trackFacebookCustomEvent("BILLING_DURATION_SELECTED", {
      duration_months: months,
      content_name: "ShopKaBot plans",
    });
  };

  const submitCheckout = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedProduct || !quote) return;
    if (busy) return;
    trackBuyEvent("checkout_initiated", {
      product_code: selectedProduct.code,
      duration_months: duration,
      value: quote.totalPaise / 100,
      currency: "INR",
    });
    const form = new FormData(event.currentTarget);
    const checkoutSessionId = sessionStorage.getItem(CHECKOUT_SESSION_KEY) || newId();
    sessionStorage.setItem(CHECKOUT_SESSION_KEY, checkoutSessionId);
    setBusy(true);
    setCheckoutError("");
    try {
      const checkout = await createCommerceCheckout({
        checkoutSessionId,
        productCode: selectedProduct.code,
        durationMonths: duration,
        couponCode: coupon.trim(),
        addonQuantities: {},
        customerName: String(form.get("customerName") || ""),
        businessName: String(form.get("businessName") || ""),
        email: String(form.get("email") || ""),
        mobile: String(form.get("mobile") || ""),
        country: "IN",
        gstin: String(form.get("gstin") || ""),
        billingAddress: String(form.get("billingAddress") || ""),
        acceptedTerms: form.get("acceptedTerms") === "on",
        attribution,
      });
      localStorage.setItem(
        ACTIVE_ORDER_KEY,
        JSON.stringify({
          orderId: checkout.order.orderId,
          statusToken: checkout.statusToken,
        }),
      );
      await handlePayment(checkout);
    } catch (error) {
      setCheckoutError(apiError(error, "Checkout could not be started."));
      setBusy(false);
      trackCommerceEvent("checkout_error", attribution, {
        productCode: selectedProduct.code,
        durationMonths: duration,
      });
    }
  };

  return (
    <div className="commerce-page">
      <Seo
        title="ShopKaBot Plans — Buy WhatsApp Automation Online"
        description="Explore ShopKaBot plans for WhatsApp marketing, chatbot automation and AI. Review transparent pricing, pay securely online and get assisted setup."
        path="/buy"
        keywords={["WhatsApp automation pricing", "WhatsApp chatbot India", "ShopKaBot plans"]}
      />
      <a className="commerce-skip-link" href="#plans">Skip to plans</a>
      <StoreHeader count={selectedProduct ? 1 : 0} onCart={() => setCartOpen(true)} />

      <main>
        <section className="commerce-hero" id="commerce-hero">
          <div className="commerce-hero-copy">
            <p className="commerce-hero-label"><MessageCircleMore size={17} aria-hidden="true" /> The ShopKaBot plan store</p>
            <h1>Your next chapter.<br />Powered by WhatsApp.</h1>
            <p className="commerce-lede">Find the right tools to reach your customers, simplify conversations and grow your business. We'll help you get set up.</p>
            <a className="commerce-button commerce-button-primary" href="#most-popular-plan" onClick={trackTrialClick}>Explore plans <ArrowRight size={18} aria-hidden="true" /></a>
            <p className="commerce-hero-note"><ShieldCheck size={16} aria-hidden="true" /> Built for the official WhatsApp Business Platform</p>
          </div>
          <aside className="commerce-hero-display" aria-label="From customer message to helpful response: an illustrative conversation">
            <div className="commerce-conversation-head"><span><MessageCircleMore size={23} aria-hidden="true" /></span><div><strong>A little less busywork.</strong><p>A lot more connection.</p></div></div>
            <div className="commerce-conversation-message">Hi! Can you help me find the right plan?</div>
            <div className="commerce-conversation-reply"><span className="commerce-reply-avatar"><Bot size={20} aria-hidden="true" /></span><p>Of course. Let's find what works for your business.</p></div>
            <div className="commerce-conversation-path"><span><Megaphone size={18} aria-hidden="true" /> Reach</span><span><Workflow size={18} aria-hidden="true" /> Automate</span><span><Bot size={18} aria-hidden="true" /> Assist</span></div>
            <p className="commerce-display-caption">Your business, better connected.</p>
          </aside>
        </section>

        <div className="commerce-reassurance" aria-label="Purchase benefits">
          <span><ShieldCheck size={19} aria-hidden="true" /> Secure Razorpay payment</span>
          <span><Headphones size={19} aria-hidden="true" /> Assisted setup included</span>
          <span><CheckCircle2 size={19} aria-hidden="true" /> GST shown before payment</span>
        </div>

        <section className="commerce-section commerce-plans" id="plans" aria-labelledby="plans-title">
          <div className="commerce-catalog-heading"><div><h2 id="plans-title">A plan for the way you work.</h2><p>Start with a one-month trial. Build from there.</p></div><a className="commerce-help-link" href={catalog?.support.whatsappUrl || "/contact-us"}><Headphones size={18} aria-hidden="true" /> Need help choosing?</a></div>
          <div className="commerce-shop-toolbar">
            <div className="commerce-categories" role="group" aria-label="Plan categories">
              {CATEGORIES.map((item) => <button type="button" key={item.code} aria-pressed={category === item.code} className={category === item.code ? "selected" : ""} onClick={() => setCategory(item.code)}>{item.name}</button>)}
            </div>
            <div className="commerce-search"><Search size={19} aria-hidden="true" /><input type="search" aria-label="Search plans" placeholder="Search plans or features" value={search} onChange={(event) => setSearch(event.target.value)} />{search ? <button type="button" aria-label="Clear search" onClick={() => setSearch("")}><X size={18} aria-hidden="true" /></button> : null}</div>
          </div>
          <div className="commerce-billing-row">
            <div className="commerce-duration-picker" role="group" aria-label="Plan duration">
              {DURATIONS.map((months) => <button type="button" className={duration === months ? "selected" : ""} aria-pressed={duration === months} disabled={months !== 1} title={months === 1 ? "Available for your trial" : "Available after your 1-month trial"} onClick={() => selectDuration(months)} key={months}>{months} {months === 1 ? "month" : "months"}{months === 1 ? <span>Trial</span> : null}</button>)}
            </div>
            <p>Longer plans available after your trial.</p>
          </div>
          <p className="commerce-result-count" role="status">{catalogLoading ? "Loading current plans..." : catalogError ? "Plans could not be loaded" : `${visibleProducts.length} ${visibleProducts.length === 1 ? "plan" : "plans"}${category !== "all" || search.trim() ? " found" : " to explore"}`}</p>
          {catalogLoading ? <div className="commerce-plan-grid" aria-hidden="true">{[1, 2, 3].map((item) => <div className="commerce-skeleton-card" key={item}><div /><span /><span /><span /><b /></div>)}</div>
            : catalogError ? <div className="commerce-empty commerce-error" role="alert"><ShieldCheck size={30} aria-hidden="true" /><h3>We couldn't load the plans</h3><p>{catalogError}</p><button className="commerce-button commerce-button-primary" type="button" onClick={loadCatalog}>Try again</button></div>
              : visibleProducts.length ? <div className="commerce-plan-grid">{visibleProducts.map((product) => <PlanCard key={product.code} product={product} duration={duration} marketingFeatures={marketingFeatures} inCart={selectedProduct?.code === product.code} onChoose={() => chooseProduct(product)} onCart={() => setCartOpen(true)} />)}</div>
                : <div className="commerce-empty"><Search size={32} aria-hidden="true" /><h3>{catalog?.products.length ? "No plans match your search" : "Our plans are being updated"}</h3><p>{catalog?.products.length ? "Try another feature or browse all plans to find your fit." : "Check back soon, or contact us for help choosing a plan."}</p>{catalog?.products.length ? <button className="commerce-button commerce-button-outline" type="button" onClick={() => { setSearch(""); setCategory("all"); }}>Clear filters</button> : <a className="commerce-button commerce-button-outline" href={catalog?.support.whatsappUrl || "/contact-us"}>Contact support</a>}</div>}
          <p className="commerce-pricing-note">{catalog?.whatsappUsageDisclaimer || "Meta/WhatsApp messaging charges are billed separately based on usage."}</p>
        </section>

        <section className="commerce-section commerce-setup" id="setup" aria-labelledby="setup-title">
          <div className="commerce-section-heading"><h2 id="setup-title">From your cart to your first conversation.</h2><p>Software is only the start. We help you put it to work.</p></div>
          <ol className="commerce-setup-grid">
            <li><span>1</span><h3>Find your fit</h3><p>Choose a plan and review your order, including applicable GST, before paying.</p></li>
            <li><span>2</span><h3>Make it yours</h3><p>Create your business account. Our team helps configure your campaigns and automation.</p></li>
            <li><span>3</span><h3>Connect and grow</h3><p>Complete your business profile, connect WhatsApp and work with us to get ready for launch.</p></li>
          </ol>
        </section>

        <section className="commerce-section commerce-faq" id="faq" aria-labelledby="faq-title">
          <div className="commerce-section-heading"><h2 id="faq-title">A few things to know.</h2><p>A clear answer before you make a choice.</p><a className="commerce-help-link" href={catalog?.support.whatsappUrl || "/contact-us"}><MessageCircleMore size={18} aria-hidden="true" /> Talk to our team</a></div>
          <div>
            {[
              ["What happens after my one-month trial?", "After your trial, you can continue with a 3-, 6-, or 12-month plan. Only the one-month trial is available for your first purchase."],
              ["Are WhatsApp messaging charges included?", "No. Meta/WhatsApp messaging charges are billed separately based on actual usage. Your plan covers platform access and its included features."],
              ["Will you help me get set up?", "Yes. Our team assists with configuration and onboarding. You don't need coding knowledge to get started."],
              ["Can I use my existing WhatsApp number?", "We review your number and Meta business setup during onboarding. Eligibility and connection steps depend on WhatsApp Business Platform requirements."],
              ["How does pricing and GST work?", "Plan prices are shown before GST. Your cart and checkout show the plan amount, setup fees, applicable GST and final payable total before payment."],
              ["Can I upgrade later?", "Yes. You can move to a higher plan as your requirements grow, subject to the applicable subscription terms."],
            ].map(([question, answer]) => <details key={question}><summary>{question}<ChevronDown size={19} aria-hidden="true" /></summary><p>{answer}</p></details>)}
          </div>
        </section>
      </main>

      {heroPassed && selectedProduct ? <div className="commerce-mobile-cart"><div><strong>{selectedProduct.name}</strong><span>{quote ? `${money(quote.totalPaise)} incl. GST` : "Review your plan"}</span></div><button className="commerce-button commerce-button-primary" type="button" onClick={() => setCartOpen(true)}>View cart <ShoppingBag size={18} aria-hidden="true" /></button></div> : null}
      <footer className="commerce-footer"><div><Link className="commerce-brand" to="/"><img src="/shopkabot-wordmark.svg" alt="ShopKaBot" width="144" height="36" /></Link><p>Better conversations. More possibilities.</p></div><nav aria-label="Store policies"><Link to="/privacy-policy">Privacy</Link><Link to="/terms-of-service">Terms</Link><Link to="/contact-us">Support</Link></nav><small>WhatsApp usage billed separately.</small></footer>

      <Dialog open={cartOpen} onClose={() => setCartOpen(false)} maxWidth="sm" fullWidth aria-labelledby="commerce-cart-title" classes={{ paper: "commerce-cart-paper" }}>
        <div className="commerce-dialog-toolbar"><h2 id="commerce-cart-title" aria-live="polite" aria-atomic="true">Your cart <span>{selectedProduct ? "1 plan" : "0 plans"}</span></h2><button className="commerce-dialog-close" type="button" aria-label="Close cart" onClick={() => setCartOpen(false)}><X aria-hidden="true" /></button></div>
        <DialogContent className="commerce-cart-content">
          {selectedProduct ? <>
            <p className="commerce-cart-intro">One plan, ready for your next chapter. Adding another plan replaces this selection.</p>
            <div className="commerce-cart-item"><span><ShoppingBag size={23} aria-hidden="true" /></span><div><h3>{selectedProduct.name}</h3><p>{duration}-month prepaid plan</p></div><button type="button" aria-label={`Remove ${selectedProduct.name} from cart`} onClick={removeFromCart}><Trash2 size={19} aria-hidden="true" /></button></div>
            {quoteLoading ? <p role="status" className="commerce-quote-loading">Calculating your total...</p> : quoteError ? <div className="commerce-form-error" role="alert"><p>{quoteError}</p><button type="button" className="commerce-button commerce-button-outline" onClick={() => loadPlanQuote(selectedProduct, duration)}>Try again</button></div> : null}
            {quote ? <dl className="commerce-cart-totals"><div><dt>Plan</dt><dd>{money(quote.planAmountPaise)}</dd></div><div><dt>Setup fee</dt><dd>{quote.setupFeePaise ? money(quote.setupFeePaise) : "Included"}</dd></div>{quote.discountPaise ? <div><dt>Discount</dt><dd>-{money(quote.discountPaise)}</dd></div> : null}<div><dt>GST ({quote.taxRateBps / 100}%)</dt><dd>{money(quote.taxPaise)}</dd></div><div className="total"><dt>Total to pay</dt><dd>{money(quote.totalPaise)}</dd></div></dl> : null}
            <p className="commerce-cart-disclaimer">WhatsApp usage is billed separately. You can add a coupon at checkout.</p>
            <button className="commerce-button commerce-button-primary commerce-pay" type="button" disabled={!quote || quoteLoading || Boolean(quoteError)} onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}>Continue to checkout <ArrowRight size={18} aria-hidden="true" /></button>
            <p className="commerce-secure-note"><ShieldCheck size={15} aria-hidden="true" /> Secure payment with Razorpay</p>
          </> : <div className="commerce-empty"><ShoppingBag size={36} aria-hidden="true" /><h3>Your cart is empty</h3><p>Explore the plans and add the one that fits your business.</p><button className="commerce-button commerce-button-primary" type="button" ref={browsePlansButton} onClick={() => { setCartOpen(false); document.getElementById("plans")?.scrollIntoView({ behavior: "auto" }); }}>Browse plans</button></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={checkoutOpen} onClose={() => !busy && setCheckoutOpen(false)} maxWidth="md" fullWidth aria-labelledby="commerce-checkout-title" classes={{ paper: "commerce-dialog-paper" }}>
        <div className="commerce-dialog-toolbar"><button className="commerce-back-link" type="button" disabled={busy} onClick={() => { setCheckoutOpen(false); setCartOpen(true); }}>Back to cart</button><button className="commerce-dialog-close" type="button" aria-label="Close checkout" onClick={() => setCheckoutOpen(false)} disabled={busy}><X aria-hidden="true" /></button></div>
        <DialogContent className="commerce-dialog-content">
          <section className="commerce-checkout-form">
            <p className="commerce-eyebrow">Secure checkout</p>
            <h2 id="commerce-checkout-title">Start your ShopKaBot setup</h2>
            <p>We only ask for what is needed to create your business account and payment.</p>
            <form onSubmit={submitCheckout}>
              <div className="commerce-field-row"><label>Full name<input name="customerName" autoComplete="name" required minLength={2} /></label><label>Business name<input name="businessName" autoComplete="organization" required minLength={2} /></label></div>
              <div className="commerce-field-row"><label>Work email<input name="email" type="email" autoComplete="email" required /></label><label>Mobile number<input name="mobile" type="tel" inputMode="tel" autoComplete="tel" required minLength={8} /></label></div>
              <div className="commerce-field-row compact"><label>Country<select name="country" defaultValue="IN"><option value="IN">India</option></select></label><label>GSTIN <em>(optional)</em><input name="gstin" maxLength={20} /></label></div>
              <label>Billing address <em>(optional)</em><textarea name="billingAddress" rows={2} autoComplete="street-address" /></label>
              <div className="commerce-coupon-row"><label>Coupon code <em>(optional)</em><input value={coupon} onChange={(event) => setCoupon(event.target.value.toUpperCase())} /></label><button className="commerce-button commerce-button-outline" type="button" onClick={applyCoupon} disabled={busy || !quote}>Apply</button></div>
              <label className="commerce-check"><input name="acceptedTerms" type="checkbox" required /><span>I agree to the <Link to="/terms-of-service" target="_blank">service terms</Link>, <Link to="/privacy-policy" target="_blank">privacy policy</Link> and selected plan pricing.</span></label>
              {checkoutError ? <div className="commerce-form-error" role="alert">{checkoutError}</div> : null}
              <button className="commerce-button commerce-button-primary commerce-pay" type="submit" disabled={busy || !quote}>{busy ? "Preparing secure payment…" : "Continue to secure payment"}</button>
              <small className="commerce-secure-note"><ShieldCheck size={14} aria-hidden="true" /> Payment is verified with Razorpay before your plan activates.</small>
            </form>
          </section>
          <aside className="commerce-order-summary">
            <p className="commerce-eyebrow">Your order</p><h3>{quote?.productName || "Loading plan…"}</h3>
            {quote ? <p>{quote.durationMonths}-month prepaid plan · {monthlyEquivalentMoney(quote.monthlyEquivalentPaise)}/month equivalent</p> : null}
            <dl><div><dt>Plan</dt><dd>{quote ? money(quote.planAmountPaise) : "—"}</dd></div><div><dt>Setup fee</dt><dd>{quote?.setupFeePaise ? money(quote.setupFeePaise) : "Included"}</dd></div>{quote?.discountPaise ? <div className="discount"><dt>Discount</dt><dd>−{money(quote.discountPaise)}</dd></div> : null}<div><dt>GST</dt><dd>{quote ? `${money(quote.taxPaise)} (${quote.taxRateBps / 100}%)` : "—"}</dd></div><div className="total"><dt>Pay now</dt><dd>{quote ? money(quote.totalPaise) : "—"}</dd></div></dl>
            <ul><li>Activates after verified payment</li><li>Implementation tracked in your workspace</li><li>WhatsApp usage billed separately</li></ul>
          </aside>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(successOrder)} onClose={() => undefined} maxWidth="sm" fullWidth aria-labelledby="commerce-success-title" classes={{ paper: "commerce-success-paper" }}>
        <DialogContent>
          <div className="commerce-success-head"><span><Check aria-hidden="true" /></span><div><p className="commerce-eyebrow">Payment confirmed</p><h2 id="commerce-success-title">Your ShopKaBot plan is active</h2><p>{successOrder?.product.name} · {successOrder?.product.durationMonths}-month plan</p></div></div>
          <div className="commerce-progress-card"><div><strong>Setup progress</strong><b>25%</b></div><progress value="25" max="100">25%</progress><ol><li className="complete">Account created</li><li className="complete">Payment complete</li><li className="current">Complete business profile</li><li>Connect WhatsApp</li><li>ShopKaBot configuration</li><li>Review and go live</li></ol></div>
          <p className="commerce-next-step"><strong>Your next step:</strong> Sign in to complete your business profile and WhatsApp connection details.</p>
          <button
            className="commerce-button commerce-button-primary commerce-pay"
            type="button"
            onClick={() =>
              catalog?.portalUrl
                ? window.location.assign(catalog.portalUrl)
                : navigate("/dashboard")
            }
          >
            Continue to your workspace
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ShopKaBotStorefront;
